import { useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm"; // GFM tables — without this, pipe tables render as raw text
import { markdownComponents } from "@/ui/CommunicationView/ChatConversation/PromptMessageContainer/markdown";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { AppShellV8 } from "../AppShellV8";
import { getActiveClient, getCurrentMemberId } from "../activeClient";
import { EntityPicker } from "../EntityPicker";
import { FriendlyLoading } from "../FriendlyLoading";
import { personName } from "../people";
import { splitAssets } from "../assetSegments";
import { AssetDocument } from "../assetRenderers";

// AMB-400: KB markdown tables used to bleed past the paper width. Wrap each table so it
// can never exceed the container — cells wrap (preferred), with horizontal scroll only as
// a fallback for genuinely too-wide tables. (The chat-shared markdownComponents don't wrap.)
const kbMarkdownComponents = {
  ...markdownComponents,
  table: ({ children }: any) => (
    <div className="my-3 max-w-full overflow-x-auto">
      {/* table-fixed → columns share the width and cell text wraps (Kyle's preferred
          behavior); the overflow-x-auto wrapper is the scroll fallback for very wide tables. */}
      <table className="w-full table-fixed">{children}</table>
    </div>
  ),
};

/**
 * Knowledge Base — the docs browser. A deliverable IS a KB doc, so the vault tree
 * is the client's deliverables grouped by campaign, and the content pane renders
 * the selected doc's real markdown body. One /deliverables call returns every
 * doc + body, so selecting a doc is instant (no per-click round trip).
 */

const CLIENT_SLUG = getActiveClient().slug;

const TYPE_LABEL: Record<string, string> = {
  email: "Email", page: "Page", social: "Social", report: "Report",
  doc: "Doc", ad: "Ad", script: "Script", data: "Data", config: "Config",
};

// Owner/person lookup is the shared, normalized one in ../people (AMB-341).

const prettify = (s?: string | null) =>
  (s || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

function statusChip(status?: string | null, reviewState?: string | null): { label: string; cls: string } {
  if (status === "archived") return { label: "Archived", cls: "bg-gray-100 text-gray-500" };
  if (status === "blocked") return { label: "Blocked", cls: "bg-red-50 text-red-700" };
  if (reviewState === "rejected") return { label: "Rejected", cls: "bg-red-50 text-red-700" };
  if (reviewState === "changes-requested") return { label: "Changes Requested", cls: "bg-amber-50 text-amber-700" };
  if (reviewState === "in-review") return { label: "In Review", cls: "bg-amber-50 text-amber-700" };
  if (status === "deployed") return { label: "Deployed", cls: "bg-emerald-50 text-emerald-700" };
  if (status === "secured") return { label: "Secured", cls: "bg-emerald-50 text-emerald-700" };
  if (reviewState === "approved") return { label: "Approved", cls: "bg-emerald-50 text-emerald-700" };
  if (status === "drafted") return { label: "Draft", cls: "bg-gray-100 text-gray-600" };
  return { label: "To Draft", cls: "bg-gray-100 text-gray-500" };
}

interface ApiDeliverable {
  id: string;
  title: string;
  deliverableType?: string;
  campaign?: string | null;
  owner?: string | null;
  due?: string | null;
  status?: string | null;
  reviewState?: string | null;
  foundation?: boolean;
  body?: string;
}

function DocIcon() {
  return (
    <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

export function KnowledgeBase() {
  const [params] = useSearchParams();
  const preselectId = params.get("id");

  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState<ApiDeliverable[]>([]);
  const [activeId, setActiveId] = useState<string | null>(preselectId);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [menuOpen, setMenuOpen] = useState(false); // F2: document toolbar "⋯ More" overflow menu
  // D-9 widen toggle. AMB-405: persist the choice in localStorage so widen/narrow sticks
  // across documents and sessions.
  const [wide, setWide] = useState<boolean>(() => {
    try { return localStorage.getItem("agents.kb.wide") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("agents.kb.wide", wide ? "1" : "0"); } catch { /* ignore */ }
  }, [wide]);
  // AMB-406: "Full view" hides the folder/file list so the doc gets the whole pane —
  // a single edit surface (the in-place Edit), no separate full-page editor.
  const [fullView, setFullView] = useState(false);
  const [campaigns, setCampaigns] = useState<Array<{ id: string; name?: string; foundation?: string[] }>>([]); // F5: per-campaign foundation doc ids

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverables?client=${CLIENT_SLUG}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { items: ApiDeliverable[] }) => {
        if (cancelled) return;
        const items = d.items || [];
        setDocs(items);
        setActiveId((cur) => cur || items[0]?.id || null);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setDocs([]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // F5: load campaigns to surface each campaign's foundation docs (foundation: string[]).
  const reloadCampaigns = () =>
    fetch(`${DOCS_API_DOMAIN}/api/agents/campaigns?client=${CLIENT_SLUG}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data) setCampaigns(data.items || []); })
      .catch(() => {});
  useEffect(() => { reloadCampaigns(); }, []);

  // Filter by search, then group: Foundation docs first, then by campaign.
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q ? docs.filter((d) => d.title.toLowerCase().includes(q)) : docs;
    const foundation = filtered.filter((d) => d.foundation);
    const byCampaign: Record<string, ApiDeliverable[]> = {};
    for (const d of filtered) {
      if (d.foundation) continue; // foundation docs live in their own group
      const key = d.campaign || "uncategorized";
      (byCampaign[key] ||= []).push(d);
    }
    const out: { campaign: string; label: string; items: ApiDeliverable[] }[] = [];
    if (foundation.length) out.push({ campaign: "__foundation", label: "★ Foundation", items: foundation });
    for (const [campaign, items] of Object.entries(byCampaign)) {
      out.push({ campaign, label: campaign === "uncategorized" ? "Uncategorized" : prettify(campaign), items });
    }
    return out;
  }, [docs, query]);

  const active = docs.find((d) => d.id === activeId) || null;

  // F5: map campaignId → foundation deliverable ids, plus a flat set for sidebar ★ markers,
  // and the resolved doc objects for the active doc's campaign.
  const campFoundationByCampaign = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const c of campaigns) if (c.id) m[c.id] = Array.isArray(c.foundation) ? c.foundation : [];
    return m;
  }, [campaigns]);
  const allCampFoundationIds = useMemo(() => new Set(Object.values(campFoundationByCampaign).flat()), [campFoundationByCampaign]);
  const activeCampFoundationDocs = useMemo(() => {
    const ids = (active?.campaign && campFoundationByCampaign[active.campaign]) || [];
    return ids.map((id) => docs.find((d) => d.id === id)).filter((d): d is ApiDeliverable => !!d);
  }, [active, campFoundationByCampaign, docs]);

  // Pin/unpin the active doc as a Foundation doc (writes frontmatter + commit).
  const [pinning, setPinning] = useState(false);
  const toggleFoundation = () => {
    if (!active) return;
    const next = !active.foundation;
    setPinning(true);
    setDocs((prev) => prev.map((d) => (d.id === active.id ? { ...d, foundation: next } : d))); // optimistic
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/foundation`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CLIENT_SLUG, id: active.id, foundation: next }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
      })
      .catch(() => {
        setDocs((prev) => prev.map((d) => (d.id === active.id ? { ...d, foundation: !next } : d))); // roll back
      })
      .finally(() => setPinning(false));
  };

  // Edit the doc's markdown body (simple textarea; last-write-wins).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const startEdit = () => {
    if (!active) return;
    setDraft(active.body || "");
    setEditing(true);
  };
  const saveEdit = () => {
    if (!active) return;
    const id = active.id;
    const body = draft;
    setSaving(true);
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/content`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CLIENT_SLUG, id, body }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, body } : d)));
        setEditing(false);
      })
      .catch(() => {/* optimistic-off: keep editing so the user can retry */})
      .finally(() => setSaving(false));
  };

  // KB file management (D9): create + delete docs. Folders = campaigns; a new doc
  // lands in the active doc's campaign (or unfiled). Refetch keeps the tree honest.
  const reloadDocs = () =>
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverables?client=${CLIENT_SLUG}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setDocs(d.items || []); })
      .catch(() => {});
  const createDoc = async () => {
    const r = await fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CLIENT_SLUG, fields: { title: "Untitled doc", campaign: active?.campaign || null, createdBy: getCurrentMemberId() } }),
    }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    await reloadDocs();
    if (r?.id) { setActiveId(r.id); setDraft(""); setEditing(true); }
  };
  const deleteDoc = async (id: string) => {
    if (!window.confirm("Delete this document? This can't be undone.")) return;
    await fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CLIENT_SLUG, id }),
    }).catch(() => {});
    await reloadDocs();
    if (activeId === id) setActiveId(null);
  };
  // Rename (title) + move (campaign/folder) — basic file management (D9).
  const renameDoc = async (id: string, current: string) => {
    const title = window.prompt("Rename document", current);
    if (!title || !title.trim() || title.trim() === current) return;
    await fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CLIENT_SLUG, id, fields: { title: title.trim() } }),
    }).catch(() => {});
    await reloadDocs();
  };
  const [movePickOpen, setMovePickOpen] = useState(false);
  const moveDoc = async (campaignId: string) => {
    if (!active) return;
    await fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CLIENT_SLUG, id: active.id, fields: { campaign: campaignId } }),
    }).catch(() => {});
    setMovePickOpen(false);
    await reloadDocs();
  };

  // Create a new folder = a new campaign grouping (folders are campaigns here).
  // Rejected server-side when the active client is the agency "all" view; surface
  // the returned error rather than failing silently.
  const [creatingFolder, setCreatingFolder] = useState(false);
  const createFolder = async () => {
    const name = window.prompt("New folder name (a campaign grouping)");
    if (!name || !name.trim()) return;
    setCreatingFolder(true);
    try {
      const res = await fetch(`${DOCS_API_DOMAIN}/api/agents/campaign/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client: CLIENT_SLUG, fields: { name: name.trim() } }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        window.alert(err?.error || err?.message || `Couldn't create folder (${res.status}).`);
        return;
      }
      await reloadDocs();
    } catch {
      window.alert("Couldn't create folder. Please try again.");
    } finally {
      setCreatingFolder(false);
    }
  };

  // Mark the active doc as a per-campaign foundation doc. Pick a campaign, read its
  // current foundation array (from the same /campaigns list the move picker uses),
  // then append this doc's id (deduped) via the generic entity/update endpoint.
  const [campFoundationOpen, setCampFoundationOpen] = useState(false);
  const setCampaignFoundation = async (campaignId: string) => {
    if (!active) return;
    const thisDocId = active.id;
    setCampFoundationOpen(false);
    try {
      const list = await fetch(`${DOCS_API_DOMAIN}/api/agents/campaigns?client=${CLIENT_SLUG}`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      const camp = (list?.items || []).find((c: any) => c.id === campaignId);
      const existing: string[] = Array.isArray(camp?.foundation) ? camp.foundation : [];
      const foundation = Array.from(new Set([...existing, thisDocId]));
      const res = await fetch(`${DOCS_API_DOMAIN}/api/agents/entity/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client: CLIENT_SLUG, type: "campaign", id: campaignId, fields: { foundation } }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        window.alert(err?.error || err?.message || `Couldn't set campaign foundation (${res.status}).`);
        return;
      }
      window.alert("Set as campaign foundation.");
      await reloadDocs();
      await reloadCampaigns();
    } catch {
      window.alert("Couldn't set campaign foundation. Please try again.");
    }
  };

  return (
    <AppShellV8 breadcrumb={["Knowledge Base"]}>
      <div className="flex h-full">
        {/* Vault tree — hidden in Full view (AMB-406) to give the doc the whole pane */}
        {!fullView && (
        <aside className="flex w-64 shrink-0 flex-col border-r border-black/[0.06] bg-gray-50/40">
          <div className="border-b border-black/[0.06] p-3">
            <div className="relative">
              <svg className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search docs…"
                className="w-full rounded-lg border border-black/[0.06] bg-white py-1.5 pl-8 pr-2 text-xs text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="mt-2 flex gap-1.5">
              <button
                data-testid="kb-new-doc"
                onClick={createDoc}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-gray-900 px-2.5 py-1.5 text-[11px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.98]"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                New doc
              </button>
              <button
                data-testid="kb-new-folder"
                onClick={createFolder}
                disabled={creatingFolder}
                title="Create a new folder (a campaign grouping)"
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-black/[0.08] bg-white px-2.5 py-1.5 text-[11px] font-medium text-gray-700 shadow-sm transition-all duration-150 hover:bg-gray-50 active:scale-[0.98] disabled:opacity-50"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" /></svg>
                {creatingFolder ? "Creating…" : "New folder"}
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-2">
            {loading ? (
              <p className="px-2 py-4 text-xs text-gray-400">Opening the knowledge base…</p>
            ) : groups.length === 0 ? (
              <p className="px-2 py-4 text-xs text-gray-400">No documents found.</p>
            ) : (
              groups.map((g) => {
                const isCollapsed = collapsed[g.campaign];
                return (
                  <div key={g.campaign} className="mb-1">
                    <button
                      onClick={() => setCollapsed((p) => ({ ...p, [g.campaign]: !p[g.campaign] }))}
                      className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400 transition-all duration-150 hover:bg-gray-100"
                    >
                      <svg className={`h-3 w-3 transition-transform duration-150 ${isCollapsed ? "" : "rotate-90"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                      {g.label}
                      <span className="ml-auto text-[10px] font-medium normal-case text-gray-300">{g.items.length}</span>
                    </button>
                    {!isCollapsed && (
                      <div className="mt-0.5 space-y-0.5">
                        {g.items.map((d) => (
                          <button
                            key={d.id}
                            onClick={() => setActiveId(d.id)}
                            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] transition-all duration-150 ${
                              d.id === activeId ? "bg-white font-medium text-gray-900 shadow-sm ring-1 ring-black/[0.04]" : "text-gray-600 hover:bg-gray-100"
                            }`}
                          >
                            <DocIcon />
                            <span className="truncate">{d.title}</span>
                            {allCampFoundationIds.has(d.id) && <span title="Campaign foundation doc" className="ml-auto shrink-0 text-[11px] text-amber-500">★</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </aside>
        )}

        {/* Content pane */}
        {/* D-9: soft-gray backdrop so the document reads as white "paper" floating on it. */}
        <div className="flex-1 overflow-auto bg-gray-50/60">
          {!active ? (
            <div className="flex h-full items-center justify-center">
              {loading ? <FriendlyLoading message="Opening the knowledge base…" /> : <p className="text-[13px] text-gray-400">Select a document.</p>}
            </div>
          ) : (
            <div className={`mx-auto my-6 rounded-xl bg-white px-8 py-8 shadow-sm ring-1 ring-black/[0.04] transition-all duration-200 ${wide ? "max-w-5xl" : "max-w-3xl"}`}>
              {/* Header */}
              <div className="mb-6 border-b border-black/[0.06] pb-5">
                <div className="mb-2.5 flex items-center gap-2">
                  <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">{TYPE_LABEL[active.deliverableType || "doc"] || "Doc"}</span>
                  {(() => {
                    const chip = statusChip(active.status, active.reviewState);
                    return <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${chip.cls}`}>{chip.label}</span>;
                  })()}
                  {active.foundation && (
                    <span title="This is a Foundation doc" className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200">★ Foundation</span>
                  )}
                  {/* F2: Edit + Open full view stay visible primary actions; Rename / Move / foundation / Delete live in ⋯ More. */}
                  <div className="ml-auto flex items-center gap-2">
                    {editing ? (
                      <>
                        <button onClick={() => setEditing(false)} disabled={saving} className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-500 transition-all duration-150 hover:bg-gray-100 disabled:opacity-50">Cancel</button>
                        <button onClick={saveEdit} disabled={saving} className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97] disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setWide((w) => !w)} title={wide ? "Narrow the page" : "Widen the page (for wide tables)"} className="rounded-lg border border-black/[0.06] bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-all duration-150 hover:bg-gray-50 active:scale-[0.97]">{wide ? "Narrow" : "Widen"}</button>
                        <button data-testid="kb-edit-doc" onClick={startEdit} className="rounded-lg border border-black/[0.06] bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-all duration-150 hover:bg-gray-50 active:scale-[0.97]">Edit</button>
                        {/* AMB-406: consolidated to ONE editor (the in-place Edit above).
                            "Full view" now just hides the folder/file list for more room,
                            instead of opening a second editor page. */}
                        <button data-testid="kb-full-view" onClick={() => setFullView((v) => !v)} title={fullView ? "Show the folder & file list" : "Hide the folder & file list for a wider view"} className="rounded-lg border border-black/[0.06] bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-all duration-150 hover:bg-gray-50 active:scale-[0.97]">
                          {fullView ? "Show files" : "Full view →"}
                        </button>
                        <div className="relative">
                          <button
                            data-testid="kb-more-menu"
                            onClick={() => setMenuOpen((v) => !v)}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            title="More actions"
                            className="flex items-center rounded-lg border border-black/[0.06] bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-all duration-150 hover:bg-gray-50 active:scale-[0.97]"
                          >
                            {/* AMB-398: was a literal ⋯ char that rendered as a broken glyph
                                in many fonts; use the app's standard dots SVG instead. */}
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 6.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM12 13.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM12 20.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" /></svg>
                            <span className="ml-1">More</span>
                          </button>
                          {menuOpen && (
                            <>
                              <button aria-hidden tabIndex={-1} onClick={() => setMenuOpen(false)} className="fixed inset-0 z-10 cursor-default" />
                              <div role="menu" className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-lg border border-black/[0.08] bg-white py-1 shadow-lg ring-1 ring-black/[0.04]">
                                <button data-testid="kb-rename-doc" role="menuitem" onClick={() => { setMenuOpen(false); renameDoc(active.id, active.title); }} className="flex w-full items-center px-3 py-1.5 text-left text-[13px] text-gray-700 transition-all duration-150 hover:bg-gray-50">Rename</button>
                                <button data-testid="kb-move-doc" role="menuitem" onClick={() => { setMenuOpen(false); setMovePickOpen(true); }} className="flex w-full items-center px-3 py-1.5 text-left text-[13px] text-gray-700 transition-all duration-150 hover:bg-gray-50">Move</button>
                                <button data-testid="kb-foundation-toggle" role="menuitem" disabled={pinning} onClick={() => { setMenuOpen(false); toggleFoundation(); }} className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[13px] text-gray-700 transition-all duration-150 hover:bg-gray-50 disabled:opacity-50">
                                  <span className="text-amber-500">{active.foundation ? "★" : "☆"}</span>
                                  {active.foundation ? "Remove from foundation" : "Set as foundation"}
                                </button>
                                <button data-testid="kb-campaign-foundation" role="menuitem" onClick={() => { setMenuOpen(false); setCampFoundationOpen(true); }} title="Set this doc as a campaign's foundation" className="flex w-full items-center px-3 py-1.5 text-left text-[13px] text-gray-700 transition-all duration-150 hover:bg-gray-50">Set as campaign foundation</button>
                                <div className="my-1 border-t border-black/[0.06]" />
                                <button data-testid="kb-delete-doc" role="menuitem" onClick={() => { setMenuOpen(false); deleteDoc(active.id); }} className="flex w-full items-center px-3 py-1.5 text-left text-[13px] text-red-600 transition-all duration-150 hover:bg-red-50">Delete</button>
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900">{active.title}</h1>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-400">
                  {active.campaign && <span>Campaign: {prettify(active.campaign)}</span>}
                  <span>Owner: {personName(active.owner)}</span>
                  <span>Due {fmtDate(active.due)}</span>
                </div>
              </div>

              {/* F5: surface the active doc's campaign foundation docs as a visible, linked group. */}
              {active.campaign && activeCampFoundationDocs.length > 0 && (
                <div data-testid="kb-campaign-foundation-group" className="mb-6 rounded-xl border border-amber-200 bg-amber-50/40 p-4">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700">★ {prettify(active.campaign)} — Foundation Docs</p>
                  <div className="flex flex-wrap gap-2">
                    {activeCampFoundationDocs.map((d) => (
                      <button
                        key={d.id}
                        onClick={() => setActiveId(d.id)}
                        className={`flex items-center gap-1.5 rounded-lg border border-amber-200 bg-white px-2.5 py-1 text-[12px] font-medium shadow-sm transition-all duration-150 hover:bg-amber-50 active:scale-[0.98] ${d.id === activeId ? "ring-1 ring-amber-300" : ""}`}
                      >
                        <DocIcon />
                        <span className="max-w-[220px] truncate text-gray-700">{d.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Body */}
              {editing ? (
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="h-[60vh] w-full resize-y rounded-lg border border-black/[0.06] bg-white p-4 font-mono text-[12px] leading-relaxed text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  spellCheck={false}
                />
              ) : active.body && splitAssets(active.body).hasAssets ? (
                // Tagged doc → render scaffolding + each typed asset (fixes raw markdown leaking
                // inside the wrapper; renders email/etc. with its typed component).
                <AssetDocument body={active.body} />
              ) : active.body ? (
                // AMB-400: prose-table forced to fit + cells wrap; the table component
                // (kbMarkdownComponents) wraps each table so it never bleeds past the paper.
                <div className="prose prose-sm max-w-none text-[13px] leading-relaxed text-gray-700 prose-headings:text-gray-900 prose-a:text-blue-600 prose-table:w-full prose-table:text-xs [&_td]:break-words [&_th]:break-words [&_td]:align-top">
                  <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]} components={kbMarkdownComponents}>
                    {active.body}
                  </Markdown>
                </div>
              ) : (
                <p className="text-[13px] text-gray-400">This document has no content yet.</p>
              )}
            </div>
          )}
        </div>
      </div>
      {movePickOpen && active && (
        <EntityPicker
          title="Move to campaign"
          endpoint={`/campaigns?client=${CLIENT_SLUG}`}
          mapItem={(c) => ({ id: c.id, label: c.name || c.id, sublabel: c.status })}
          confirmLabel="Move"
          onConfirm={(ids) => moveDoc(ids[0])}
          onClose={() => setMovePickOpen(false)}
        />
      )}
      {campFoundationOpen && active && (
        <EntityPicker
          title="Set as campaign foundation"
          endpoint={`/campaigns?client=${CLIENT_SLUG}`}
          mapItem={(c) => ({ id: c.id, label: c.name || c.id, sublabel: c.status })}
          confirmLabel="Set foundation"
          onConfirm={(ids) => setCampaignFoundation(ids[0])}
          onClose={() => setCampFoundationOpen(false)}
        />
      )}
    </AppShellV8>
  );
}
